import { useMemo } from 'react'
import { useFirestoreGeneralIn } from '../../firebase/useFirestore'
import usePersonaTerms from '../../hooks/usePersonaTerms'

/**
 * Toont de namen van gekoppelde deelnemers achter elkaar.
 *
 * Zoekt de deelnemers zelf op in één query, in plaats van per naam een eigen
 * lookup te doen. Dat is niet alleen zuiniger: het maakt ook zichtbaar dat een
 * ID géén deelnemer meer oplevert. Zo'n verweesd ID (deelnemer verwijderd nadat
 * de registratie is vastgelegd) levert anders een kopje zonder namen op.
 *
 * @param {object} props
 * @param {string[]} props.personaIds - ID's van de gekoppelde deelnemers
 * @param {boolean} [props.showLabel] - Toon het kopje boven de namen
 * @param {string} [props.placeholder] - Wat te tonen als geen enkel ID een deelnemer oplevert
 * @returns {JSX.Element|null}
 */
const PersonaNameList = ({ personaIds, showLabel = false, placeholder = '' }) => {
    const personaTerms = usePersonaTerms()

    const ids = useMemo(
        () => (Array.isArray(personaIds) ? personaIds.filter(Boolean) : []),
        [personaIds]
    )

    const personas = useFirestoreGeneralIn('Personas', 'ID', ids)

    // Op de volgorde van `ids` houden; Firestore geeft een 'in'-query in
    // onvoorspelbare volgorde terug.
    const names = useMemo(() => {
        const byId = new Map((personas || []).map((persona) => [persona.ID, persona.Name]))
        return ids.map((id) => byId.get(id)).filter(Boolean)
    }, [personas, ids])

    if (names.length === 0) return placeholder ? <p>{placeholder}</p> : null

    return (
        <div className="persona-name-list">
            {showLabel && (
                <p className="persona-name-list-label">{personaTerms.plural}</p>
            )}
            <p className="persona-name-list-names">{names.join(', ')}</p>
        </div>
    )
}

export default PersonaNameList
